import type { Auction } from "@/services/types";
import { Badge } from "@/components/base/Badge";

/**
 * Eligible vs ineligible members, with a stated reason for every exclusion.
 * Staff are asked out loud why a member cannot bid — the reason is always shown.
 */
export function EligibilityPanel({ auction }: { auction: Auction }) {
  return (
    <div className="space-y-4">
      <div>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="flex items-center gap-1.5 text-sm font-semibold text-foreground-800">
            <i className="ri-checkbox-circle-line text-success-600" />
            Eligible to bid
          </h3>
          <span className="tnum rounded-full bg-success-100 px-2 py-0.5 text-xs font-medium text-success-800">{auction.eligible.length}</span>
        </div>
        {auction.eligible.length === 0 ? (
          <p className="rounded-md border border-dashed border-background-200 px-3 py-3 text-xs text-foreground-400">
            No members are eligible to bid.
          </p>
        ) : (
          <ul className="divide-y divide-background-100 rounded-lg border border-background-200">
            {auction.eligible.map((m) => (
              <li key={m.memberId} className="flex items-center justify-between gap-2 px-3 py-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-foreground-900">{m.fullName}</p>
                  <p className="tnum text-xs text-foreground-500">{m.ticketNumber} · {m.primaryPhone}</p>
                </div>
                {m.outstanding > 0 ? (
                  <Badge color="warning">Dues ₹{m.outstanding.toLocaleString("en-IN")}</Badge>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="flex items-center gap-1.5 text-sm font-semibold text-foreground-800">
            <i className="ri-close-circle-line text-danger-500" />
            Not eligible
          </h3>
          <span className="tnum rounded-full bg-danger-100 px-2 py-0.5 text-xs font-medium text-danger-700">{auction.ineligible.length}</span>
        </div>
        {auction.ineligible.length === 0 ? (
          <p className="rounded-md border border-dashed border-background-200 px-3 py-3 text-xs text-foreground-400">
            Everyone is eligible to bid.
          </p>
        ) : (
          <ul className="divide-y divide-background-100 rounded-lg border border-background-200">
            {auction.ineligible.map((m) => (
              <li key={m.memberId} className="px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground-900">{m.fullName}</p>
                    <p className="tnum text-xs text-foreground-500">{m.ticketNumber} · {m.primaryPhone}</p>
                  </div>
                </div>
                <p className="mt-1 text-xs text-danger-600">{m.reason}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}